import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import * as SplitsActions from './splits.actions';
import { SplitsService } from '../../services/splits.service';
import { selectSelectedStock } from '../stocks/stocks.selectors';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root',
})
export class SplitsEffects {
  loadSplits$: any = {};
  loadSplit$: any = {};
  createSplit$: any = {};
  updateSplit$: any = {};
  deleteSplit$: any = {};
  splitSuccess$: any = {};

  constructor(
    private actions$: Actions,
    private splitsService: SplitsService,
    private router: Router,
    private store: Store
  ) {
    this.loadSplits$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SplitsActions.loadSplits),
        switchMap(({ stockId, filters }) =>
          this.splitsService.getSplits(stockId, filters?.pageNumber, filters?.pageSize).pipe(
            map((response: any) => {
              const splits = response.data.items || response;

              const pagination = {
                currentPage: response.data.pageNumber,
                pageSize: response.data.pageSize,
                totalItems: response.data.totalCount,
              };

              return SplitsActions.loadSplitsSuccess({
                splits,
                pagination,
              });
            }),
            catchError((error) => of(SplitsActions.loadSplitsFailure({ error: error.message })))
          )
        )
      )
    );

    this.loadSplit$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SplitsActions.loadSplit),
        switchMap(({ id }) =>
          this.splitsService.getSplitById(id).pipe(
            map((response: any) => {
              const split = response.data || response;
              return SplitsActions.loadSplitSuccess({ split });
            }),
            catchError((error) => of(SplitsActions.loadSplitFailure({ error: error.message })))
          )
        )
      )
    );

    this.createSplit$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SplitsActions.createSplit),
        switchMap(({ split }) =>
          this.splitsService.createSplit(split).pipe(
            map((response: any) => {
              const createdSplit = response.data || response;
              return SplitsActions.createSplitSuccess({ split: createdSplit });
            }),
            catchError((error) => of(SplitsActions.createSplitFailure({ error: error.message })))
          )
        )
      )
    );

    this.updateSplit$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SplitsActions.updateSplit),
        switchMap(({ id, split }) =>
          this.splitsService.updateSplit(id, split).pipe(
            map((response: any) => {
              const updatedSplit = response.data || response;
              return SplitsActions.updateSplitSuccess({ split: updatedSplit });
            }),
            catchError((error) => of(SplitsActions.updateSplitFailure({ error: error.message })))
          )
        )
      )
    );

    this.deleteSplit$ = createEffect(() =>
      this.actions$.pipe(
        ofType(SplitsActions.deleteSplit),
        switchMap(({ id }) =>
          this.splitsService.deleteSplit(id).pipe(
            map(() => SplitsActions.deleteSplitSuccess({ id })),
            catchError((error) => of(SplitsActions.deleteSplitFailure({ error: error.message })))
          )
        )
      )
    );

    // Navigate after success
    this.splitSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            SplitsActions.createSplitSuccess,
            SplitsActions.updateSplitSuccess,
            SplitsActions.deleteSplitSuccess
          ),
          tap(() => {
            this.store.select(selectSelectedStock).subscribe((stock) => {
              this.router.navigate(['/stocks', 'view', stock?.id, 'split']);
            });
          })
        ),
      { dispatch: false }
    );
  }
}
